import type { Metadata } from "next";
import { Container } from "@/components/container";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { LegalNotice } from "@/components/legal-notice";
import { site } from "@/content/site";

export const metadata: Metadata = {
  title: "Disclaimer",
  description:
    "Website disclaimer for Quto AI — informational content, no warranties, and how our AI agents and demos should be understood.",
  alternates: { canonical: "/disclaimer" },
};

export default function DisclaimerPage() {
  const updated = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });

  return (
    <div className="py-12 md:py-20">
      <Container className="max-w-3xl">
        <Breadcrumbs items={[{ label: "Disclaimer" }]} className="mb-8" />
        <h1 className="font-display text-[clamp(2rem,4vw,3rem)] font-semibold tracking-tight">
          Disclaimer
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Last updated: {updated}
        </p>
        <LegalNotice className="mt-6" />

        <div className="prose prose-neutral mt-8 max-w-none dark:prose-invert prose-headings:font-display prose-a:text-foreground">
          <h2>General information</h2>
          <p>
            The information provided by {site.name} (&ldquo;we&rdquo;,
            &ldquo;us&rdquo; or &ldquo;our&rdquo;) on {site.domain} (the
            &ldquo;Site&rdquo;) is for general informational purposes only. All
            information on the Site is provided in good faith; however, we make
            no representation or warranty of any kind, express or implied,
            regarding the accuracy, adequacy, validity, reliability or
            completeness of any information on the Site.
          </p>

          <h2>Not professional advice</h2>
          <p>
            The Site cannot and does not contain legal, financial, medical or
            other professional advice. Any information is provided for general
            informational and educational purposes only and is not a substitute
            for professional advice. Consult the appropriate professional before
            acting on any information found here.
          </p>

          <h2>AI agents &amp; demos</h2>
          <p>
            Quto AI designs and deploys conversational AI agents. Sample
            conversations, audio demos and metrics shown on the Site are
            illustrative and provided to explain how our agents work. AI systems
            can make mistakes; deployed agents include human-handoff paths and
            are configured with each client. Outcomes depend on your data,
            integrations and use case, and are not guaranteed.
          </p>

          <h2>External links</h2>
          <p>
            The Site may contain links to third-party websites or content. We do
            not warrant, endorse or assume responsibility for the accuracy or
            reliability of any information offered by third-party sites.
          </p>

          <h2>Contact</h2>
          <p>
            If you have questions about this disclaimer, contact us at{" "}
            {site.email ? (
              <a href={`mailto:${site.email}`}>{site.email}</a>
            ) : (
              "our contact page"
            )}
            .
          </p>
        </div>
      </Container>
    </div>
  );
}
